import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { PagedData, PaginationMeta } from './common';

export interface Feedback {
  id: string;
  category: string;
  submitterType: 'HUMAN' | 'AI';
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'DISMISSED';
  title: string;
  content: string;
  handledAt: string | null;
  handledNote: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

type CreateFeedbackInput = {
  category: string;
  title: string;
  content: string;
};

type FeedbackPagedResponse = {
  data: Feedback[];
  meta: PaginationMeta;
};

export const fetchFeedbacksPaged = async (
  token: string,
  params?: {
    search?: string;
    status?: string;
    from?: string;
    to?: string;
    page?: number;
    pageSize?: number;
  },
): Promise<PagedData<Feedback>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.set('search', params.search);
  if (params?.status) queryParams.set('status', params.status);
  if (params?.from) queryParams.set('from', params.from);
  if (params?.to) queryParams.set('to', params.to);
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

  const url = `${getApiUrl()}/api/feedbacks${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch feedbacks: ${response.status}`);
  }

  const data = (await response.json()) as FeedbackPagedResponse;
  return data;
};

type FeedbackResponse = {
  data: Feedback;
};

export const createFeedback = async (token: string, input: CreateFeedbackInput): Promise<Feedback> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/feedbacks`, {
    method: 'POST',
    cache: 'no-store',
    headers: authHeaders(token, true),
    body: JSON.stringify({
      category: input.category,
      submitterType: 'HUMAN',
      title: input.title,
      content: input.content,
    }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create feedback: ${response.status}`);
  }

  const data = (await response.json()) as FeedbackResponse;
  return data.data;
};

export const updateFeedbackStatus = async (
  token: string,
  id: string,
  input: { status: Feedback['status'] },
): Promise<Feedback> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/feedbacks/${id}`, {
    method: 'PATCH',
    cache: 'no-store',
    headers: authHeaders(token, true),
    body: JSON.stringify({
      status: input.status,
    }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update feedback: ${response.status}`);
  }

  const data = (await response.json()) as FeedbackResponse;
  return data.data;
};
